import React, { useEffect, useRef, useState } from "react";
import { AddFlex } from "../reusableStyles";
import { callNonStoreApi } from "../NonStoreApis";
import {
  DuplicateCampaign,
  FetchAllCampaigns,
  FetchManagedCampaigns,
  ToggleCampaignStatus,
} from "../Api/ApiEndPoints";
import { useSelector } from "react-redux";
import {
  Lock,
  Unlock,
  Bot,
  Calendar,
  User,
  MoreVertical,
  Clock,
  ChevronRight,
  Video,
  Image,
  Camera,
  Loader,
} from "lucide-react";
import { useNavigate } from "react-router";
import {
  INSTAGRAM_CAMPAGIN_STATS,
  INSTAGRAM_UPDATE_MANAGED_CAMPAIGN,
  UPDATE_INSTAGRAM_CAMPAIGNS,
} from "../OnboardingPage/routeNames";
import { capitalizeFirstLetter, isStringNullOrUndefined } from "../utils";
import useInstagramIntegrationHook from "../CustomHooks/useInstagramIntegrationHook";
import { Button } from "antd";

function ManagedCampaigns() {
  const menuRef = useRef(null); // Add at the top with other state declarations

  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const { credentials } = useSelector((state) => state.instagram);
  const data = useSelector((state) => state.user);
  const [openMenuId, setOpenMenuId] = useState(null);

  const fetchCampaigns = async () => {
    try {
      setIsLoading(true);
      console.log(data);
      const response = await callNonStoreApi(FetchManagedCampaigns, {
        email: data.apiUserData.userEmail,
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchCampaigns();
  }, []);

  const getStatusColor = (status) => {
    const statusColors = {
      live: "bg-emerald-100 text-emerald-800 border-emerald-200",
      draft: "bg-gray-100 text-gray-800 border-gray-200",
      paused: "bg-amber-100 text-amber-800 border-amber-200",
      archived: "bg-red-100 text-red-800 border-red-200",
    };
    return statusColors[status] || statusColors.draft;
  };

  const getBorderColor = (status) => {
    const borderColors = {
      live: "border-l-emerald-500",
      draft: "border-l-gray-500",
      paused: "border-l-amber-500",
      archived: "border-l-red-500",
    };
    return borderColors[status] || borderColors.draft;
  };

  const handleCardClick = (e, campaign) => {
    // If clicking the menu or menu button, let those handlers work
    if (
      e.target.closest(".menu-button") ||
      e.target.closest(".dropdown-menu")
    ) {
      return;
    }
    if (openMenuId) {
      return setOpenMenuId(null);
    }
    // Otherwise navigate and close menu
    navigate(
      INSTAGRAM_UPDATE_MANAGED_CAMPAIGN.replace(":campaignId", campaign._id)
    );
    setOpenMenuId(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const checkIfInstagramIntegrated = (campaginData) => {
    const { owner, instaUserId } = campaginData;
    if (
      owner.addToAutomation &&
      isStringNullOrUndefined(owner.selectedInstagram)
    ) {
      return false;
    }
    return true;
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getAutomationCounts = (automations) => {
    return {
      reels: automations.filter((a) => !a.mediaData.isStory).length,
      stories: automations.filter((a) => a.mediaData.isStory).length,
    };
  };

  return isLoading ? (
    <Loader />
  ) : (
    <div className="container mx-auto px-0 py-0">
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
        {campaigns?.map((campaign) => {
          const automationCounts = getAutomationCounts(campaign.automations);

          return (
            <div
              key={campaign._id}
              className={`bg-white rounded-lg border shadow-sm hover:shadow-lg transition-all duration-200 border-l-4 ${getBorderColor(
                campaign.status
              )} group cursor-pointer relative`}
              onClick={(e) => handleCardClick(e, campaign)}
            >
              <div className="p-6">
                <div className="flex flex-col h-full">
                  {/* Header Section */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-xl font-semibold group-hover:text-blue-600 transition-colors">
                          {campaign.name}
                        </h2>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            campaign.status
                          )}`}
                        >
                          {capitalizeFirstLetter(campaign.status)}
                        </span>
                        {campaign.owner.addToAutomation && (
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              checkIfInstagramIntegrated(campaign)
                                ? "bg-purple-100 text-purple-800 border-purple-200"
                                : "bg-red-200 text-red-800 border-red-200"
                            }`}
                          >
                            {checkIfInstagramIntegrated(campaign)
                              ? "Instagram Linked"
                              : "Link Your Instagram"}
                          </span>
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span>Created {getTimeAgo(campaign.createdAt)}</span>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenMenuId(
                            openMenuId === campaign._id ? null : campaign._id
                          );
                        }}
                        className="hover:bg-gray-100 p-2 rounded-full"
                      >
                        <MoreVertical className="h-5 w-5 text-gray-500" />
                      </button>
                      {openMenuId === campaign._id && (
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border py-1 z-10"
                        >
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(
                                UPDATE_INSTAGRAM_CAMPAIGNS.replace(
                                  ":campaignId",
                                  campaign._id
                                )
                              );
                              setOpenMenuId(null);
                            }}
                          >
                            Edit Campaign
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsLoading(true);
                              await callNonStoreApi(DuplicateCampaign, {
                                campaignId: campaign._id,
                              });
                              await fetchCampaigns();
                              setOpenMenuId(null);
                            }}
                          >
                            Duplicate Campaign
                          </button>
                          <hr className="my-1" />
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 text-red-600"
                            onClick={async (e) => {
                              try {
                                e.preventDefault();
                                e.stopPropagation();
                                await callNonStoreApi(ToggleCampaignStatus, {
                                  campaignId: campaign._id,
                                  status:
                                    campaign.status === "live"
                                      ? "paused"
                                      : "live",
                                });
                                await fetchCampaigns();
                              } catch (error) {
                                console.log(error);
                              }
                              setOpenMenuId(null);
                            }}
                          >
                            {`${
                              campaign.status === "paused" ? "Live" : "Pause"
                            } Campaign`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Participants Count */}
                    <div className="bg-blue-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 text-sm text-blue-700 mb-1">
                        <User className="h-4 w-4" />
                        <span>Participants</span>
                      </div>
                      <p className="text-xl font-semibold text-blue-900">
                        {campaign.participantCount
                          ? campaign.participantCount
                          : 0}
                      </p>
                    </div>

                    {/* Total Automations */}
                    <div className="bg-purple-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 text-sm text-purple-700 mb-1">
                        <Bot className="h-4 w-4" />
                        <span>Automations</span>
                      </div>
                      <p className="text-xl font-semibold text-purple-900">
                        {campaign.automations.length}
                      </p>
                    </div>
                  </div>

                  {/* Automation Types */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Automation Types
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Posts / Reels</p>
                          <p className="font-semibold">
                            {automationCounts.reels}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Camera className="h-4 w-4 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Stories</p>
                          <p className="font-semibold">
                            {automationCounts.stories}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Access Section */}
                  <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-xl">
                    {campaign.inviteAccess === "Open" ? (
                      <>
                        <div className="bg-green-100 p-2 rounded-lg">
                          <Unlock className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Open Access
                          </p>
                          <p className="text-sm text-gray-600">
                            Campaign is publicly accessible
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="bg-amber-100 p-2 rounded-lg">
                          <Lock className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Limited Access
                          </p>
                          <p className="text-sm text-gray-600">
                            Invitation only campaign
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Footer Section */}
                  <div className="mt-auto mb-8 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="bg-gray-100 p-1.5 rounded-full">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-600">
                          Created by me
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(campaign.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        INSTAGRAM_CAMPAGIN_STATS.replace(
                          ":campaignId",
                          campaign._id
                        )
                      );
                    }}
                  >
                    View Stats
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ManagedCampaigns;
